import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useDispatch } from 'react-redux';

import FigoLoadingOverlay from '../../common/FigoLoadingOverlay';
import FigoInput from '../../common/FigoInput';
import { Checkbox } from '../../common/Checkbox';
import { useChangeCoverageOrcaCa } from './hooks/useChangeCoverageOrcaCa';
import { PolicyHeader } from '../PolicyHeader';
import { ChangeCoverageButtons } from './ChangeCoverageButtons';

import {
  currencyFormat,
  getOrcaStateByPostalCode,
  possesiveAdaptationI18n,
} from '../../../services/utils';
import { PET_TYPE_MULTIBRAND, PROXY_POLICY_PAYMENT_TYPE_ID } from '../../../constants';

import dogPlaceholder from '../../../assets/billingPayments/dog-placeholder.png';
import catPlaceholder from '../../../assets/billingPayments/cat-placeholder.png';
import creditCardIcon from '../../../assets/billingPayments/credit-card.svg';
import bankIcon from '../../../assets/billingPayments/saving-bank.svg';

import { saveOrcaQuoteReset, submitCoveragePeriod } from '../../../actions';
import { INSURANCE, ORCA_CHANGE_COVERAGE_SUBMIT_SUCCESS } from '../../navigation/routes.constants';
import { useUpToTop } from '../../../hooks';

import styles from './css/ChangeCoverageOrcaCheckout.module.css';

function findLimitDisplay(list, id) {
  return list?.find((option) => option.id === id)?.display ?? '-';
}

export const ChangeCoverageOrcaCheckout = () => {
  const { t } = useTranslation('changeCoverage');
  const history = useHistory();
  const dispatch = useDispatch();

  const [agreed, setAgreed] = useState(false);

  const {
    baseProducts,
    calculatePrice,
    coveragePeriodId,
    customer,
    customerId,
    defaultPolicy,
    loading,
    marketingChanelId,
    paymentManagementUrl,
    petName,
    proxyPolicy,
    quoteGuid,
    quotePreview,
    selectedLimits,
    selectedPet,
    selectedProductCode,
    selectedWellness,
    submitQuote,
    wellnessOptions,
  } = useChangeCoverageOrcaCa();

  const selectedProduct = useMemo(() => baseProducts?.find(({
    code,
  }) => code === selectedProductCode), [
    baseProducts,
    selectedProductCode,
  ]);

  const monthlyPremium = useMemo(() => {
    const quote = quotePreview[selectedProductCode];

    return calculatePrice({
      monthlyPreviewResults: quote?.monthlyPreviewResults,
      productSelected: {
        stateFilingProductId: quote?.stateFilingProductId,
      },
      wellnessSelected: selectedWellness,
    });
  }, [
    baseProducts,
    quotePreview,
    quotePreview,
    selectedLimits,
    selectedProductCode,
    selectedWellness,
  ]);

  // WIP: personalInfo.StateId?
  const province = useMemo(() => getOrcaStateByPostalCode(customer?.postalCode),
    [customer?.postalCode]);

  const { customerPaymentOption } = proxyPolicy;
  const paymentType = customerPaymentOption?.paymentTypeId;
  const bankName = customerPaymentOption.bankName;
  const cardLast4 = customerPaymentOption.last4;
  const cardExpireOn = customerPaymentOption.expiresOn
    ? moment(customerPaymentOption.expiresOn).format('MM/YY')
    : '';

  const petAvatar = useMemo(
    () => (selectedPet?.type === PET_TYPE_MULTIBRAND.CAT
      ? catPlaceholder : dogPlaceholder), [],
  );

  const onSubmitClick = () => {
    dispatch(submitCoveragePeriod({
      coveragePeriodId,
      customerId,
      managementUrl: paymentManagementUrl,
      quoteGuid,
    }));
  };

  const onBackClick = async () => {
    await dispatch(saveOrcaQuoteReset());
    history.goBack();
  };

  useUpToTop();

  useEffect(() => {
    if (submitQuote?.quoteId) {
      const url = `${ORCA_CHANGE_COVERAGE_SUBMIT_SUCCESS}?`
        .replace(':marketingChanelId', marketingChanelId);

      history.push(url);
    }
  }, [submitQuote]);

  useLayoutEffect(() => {
    if (!defaultPolicy) {
      history.replace(INSURANCE);
    }
  }, [defaultPolicy]);

  return (
    <div className={styles.checkout}>
      <h1>{t('checkout.title')}</h1>

      <div className={`Colors-${marketingChanelId} Width-full`}>
        <PolicyHeader
          company={+marketingChanelId}
          logoClass="Change-coverage-header-logo"
        >
          <div className={styles.checkoutPrice}>
            <span className={styles.checkoutPriceValue}>
              {currencyFormat({ value: monthlyPremium })}

              <span>{t('checkout.perMonth')}</span>
            </span>

            <span className={styles.checkoutPriceName}>
              {t('checkout.quoteFor', {
                count: possesiveAdaptationI18n(petName),
                name: petName,
              })}
            </span>
          </div>
        </PolicyHeader>
      </div>

      <div className={styles.checkoutContainer}>

        <div className={styles.checkoutSection}>
          <h3>{t('checkout.yourInformation')}</h3>

          <div className={styles.checkoutGrid}>
            <FigoInput
              disabled
              title={t('checkout.firstName')}
              value={customer?.firstName || ''}
            />

            <FigoInput
              disabled
              title={t('checkout.lastName')}
              value={customer?.lastName || ''}
            />

            <FigoInput
              disabled
              title={t('checkout.streetAddress')}
              value={customer?.address1 || ''}
            />

            <FigoInput
              disabled
              title={(
                <span className={styles.checkoutGridFieldTitle}>
                  <span>{t('checkout.streetAddress2')}</span>

                  <span
                    className={styles.checkoutGridFieldOptional}
                  >
                    {t('options.optional')}
                  </span>
                </span>
              )}
              value={customer?.address2 || ''}
            />

            <FigoInput
              disabled
              title={t('checkout.city')}
              value={customer?.city || ''}
            />

            <div className={styles.checkoutGridSplit}>
              <FigoInput
                disabled
                title={t('checkout.province')}
                value={province || ''}
              />

              <FigoInput
                disabled
                title={t('checkout.postalCode')}
                value={customer?.postalCode || ''}
              />
            </div>

            <FigoInput
              disabled
              title={t('checkout.phoneNumber')}
              value={customer?.phone || ''}
            />

            <FigoInput
              disabled
              title={t('checkout.email')}
              value={customer?.email || ''}
            />
          </div>
        </div>

        {customerPaymentOption?.customerPaymentOptionId && (
          <div className={styles.checkoutSection}>
            <h3>{t('checkout.billingInformation')}</h3>

            <div className={styles.checkoutBillingCard}>
              <img
                alt=""
                src={paymentType === PROXY_POLICY_PAYMENT_TYPE_ID.creditCard
                  ? creditCardIcon : bankIcon}
              />

              {paymentType === PROXY_POLICY_PAYMENT_TYPE_ID.creditCard
                ? (
                  <>
                    <span>
                      {t('checkout.cardEndingIn', { last4: cardLast4 })}
                    </span>

                    <span>
                      {t('checkout.exp', {
                        cardExpireOn,
                        interpolation: { escapeValue: false },
                      })}
                    </span>
                  </>
                ) : (
                  <span>
                    {bankName}
                  </span>
                )}

              <span>
                {t('checkout.exp', { cardExpireOn })}
              </span>
            </div>
          </div>
        )}

        {selectedProduct && (
          <div className={styles.checkoutSectionSummary}>
            <h3>{t('checkout.policyOverview')}</h3>

            <div className={styles.checkoutPet}>
              <img
                alt=""
                className={styles.checkoutPetAvatar}
                src={petAvatar}
              />

              <div>
                <h4 className={styles.checkoutPetName}>
                  {petName}
                </h4>

                <span>
                  {t('checkout.coverageType')}
                  {': '}
                  {selectedProduct.display}
                </span>
              </div>
            </div>

            <div className={styles.checkoutColumns}>
              <div className={styles.checkoutColumn}>
                <h4 className={styles.checkoutColumnTitle}>
                  {t('checkout.coverage')}
                </h4>

                <div className={styles.checkoutDetailRow}>
                  <span>{t('options.orca.annualLimit')}</span>

                  <span>
                    {findLimitDisplay(
                      selectedProduct.limits.coverageLimits,
                      selectedLimits?.coverageLimitId,
                    )}
                  </span>
                </div>

                <div className={styles.checkoutDetailRow}>
                  <span>{t('preview.deductible')}</span>

                  <span>
                    {findLimitDisplay(
                      selectedProduct.limits.deductibleLimits,
                      selectedLimits?.deductibleId,
                    )}
                  </span>
                </div>

                <div className={styles.checkoutDetailRow}>
                  <span>{t('preview.coinsurance')}</span>

                  <span>
                    {findLimitDisplay(
                      selectedProduct.limits.coinsuranceLimits,
                      selectedLimits?.coinsuranceId,
                    )}
                  </span>
                </div>

                <div className={styles.checkoutDetailRow}>
                  <span>{t('preview.incidentLimit')}</span>

                  <span>
                    {findLimitDisplay(
                      selectedProduct.limits.incidentLimits,
                      selectedLimits?.incidentLimitId,
                    )}
                  </span>
                </div>

                <div
                  className={styles.checkoutDetailRow}
                  data-total="true"
                >
                  <span>{t('options.monthlyPremium')}</span>

                  <span>{currencyFormat({ value: monthlyPremium })}</span>
                </div>

                <div className={styles.checkoutTotalContainer}>
                  <div className={styles.checkoutDetailRow}>
                    <span>{t('checkout.totalMonthlyPremium')}</span>

                    <span>{currencyFormat({ value: monthlyPremium })}</span>
                  </div>
                </div>
              </div>

              {!!wellnessOptions?.length && (
                <div className={styles.checkoutColumn}>
                  <h4 className={styles.checkoutColumnTitle}>
                    {t('options.orca.optionalWellness')}
                  </h4>

                  {wellnessOptions.map((option) => {
                    const isIncluded = option.code === selectedWellness?.code;

                    return (
                      <div
                        key={option.productId}
                        className={styles.checkoutDetailRow}
                      >
                        <span>{option.display}</span>

                        <span
                          className={isIncluded ? ''
                            : styles.checkoutValueNotIncluded}
                        >
                          {isIncluded
                            ? t('preview.included')
                            : t('preview.notIncluded')}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        <div className={styles.checkoutAgreement}>
          <Checkbox
            checked={agreed}
            id="orca-checkout-agreement"
            label={t('checkout.agreementLabel')}
            onChange={setAgreed}
          />
        </div>

        <div className={styles.checkoutButtons}>
          <ChangeCoverageButtons
            disabledPrimaryButton={!agreed}
            onClickPrimaryButton={onSubmitClick}
            onClickSecondaryButton={onBackClick}
            primaryButtonColor="var(--pc-color-blue-300)"
            primaryText={t('reviewChangesModal.submit')}
            secondaryText={t('checkout.backToCustomize')}
          />
        </div>
      </div>

      <FigoLoadingOverlay visible={loading} />
    </div>
  );
};
